import type { Genome } from '@/domain/genetics/types';
import type { BiomeId } from '@/domain/world/types';
import { hashCode, mulberry32 } from '@/domain/world/rng';
import type { EnemyRank } from './enemy-behavior';
import { enemiesForBiome } from './enemy-defs';
import { generateWildGenome } from './wild-genome';
import { deriveWildCombatProfile, type WildCombatProfile } from './wild-stats';

export type EnemySpawnSpec =
  | { kind: 'fauna'; rank: 'common'; defId: string; seed: string }
  | {
      kind: 'wild'; rank: EnemyRank; populationId: string;
      genome: Genome; profile: WildCombatProfile; seed: string;
    };

export interface ComposeEnemyPopulationInput {
  biome: BiomeId;
  runId: string;
  count: number;
}

export function composeEnemyPopulation(input: ComposeEnemyPopulationInput): EnemySpawnSpec[] {
  const count = Math.max(0, Math.floor(input.count));
  if (count === 0) return [];
  const rng = mulberry32(hashCode(`${input.biome}:${input.runId}:population`));
  const defs = enemiesForBiome(input.biome);
  const wildCount = Math.min(count, count <= 4 ? 1 : 2);
  const faunaCount = count - wildCount;
  const specs: EnemySpawnSpec[] = [];

  for (let index = 0; index < faunaCount; index++) {
    const def = defs[Math.floor(rng() * defs.length)]!;
    specs.push({ kind: 'fauna', rank: 'common', defId: def.id, seed: `${input.runId}:fauna:${index}` });
  }

  const dominantIndex = rng() < 0.125 ? Math.floor(rng() * wildCount) : -1;
  for (let index = 0; index < wildCount; index++) {
    const seed = `${input.runId}:wild:${index}`;
    const rank: EnemyRank = index === dominantIndex ? 'dominant' : 'common';
    const genome = generateWildGenome({ biome: input.biome, seed });
    specs.push({
      kind: 'wild',
      rank,
      populationId: `${input.biome}-wild`,
      genome,
      profile: deriveWildCombatProfile(genome, input.biome, rank),
      seed,
    });
  }

  for (let index = specs.length - 1; index > 0; index--) {
    const other = Math.floor(rng() * (index + 1));
    [specs[index], specs[other]] = [specs[other]!, specs[index]!];
  }
  return specs;
}
